// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O₁ · O PLANO MUDOU NOUTRO SÍTIO — e até aqui ninguém o dizia
// ============================================================================
//
// 📗 `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`, §11 O, ponto 1:
//    *«quando o mapa recarrega por causa de outro, **dizer** — não trocar o conteúdo em
//    silêncio»*. E o contexto que o torna urgente: **os planeadores da GVA estão no `staging`
//    agora, vários na mesma grelha.**
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ O QUE SE MEDIU ANTES DE ESCREVER ISTO — e MUDA o enunciado
// ---------------------------------------------------------------------------
//
//   O documento descreve o mapa a «recarregar por baixo de mim». **Não é o que o código faz**,
//   e a diferença importa porque muda qual é o defeito:
//
//     1 · `sinal-cache.ts` (`LISTEN`/`NOTIFY`) é **do lado do servidor**: invalida a cache do
//         mapa **entre instâncias da API**. Não há canal nenhum para o navegador — zero
//         `EventSource`, zero `WebSocket`, zero `setInterval` em `apps/web/src`.
//     2 · O navegador só volta a pedir `/api/mapa/dados` quando **este separador** chama
//         `recarregar` (`App.tsx`, o contador `recargas`), e isso acontece a seguir a uma
//         escrita **minha**.
//
//   📌 Logo o mapa **não troca sozinho**: fica **calado e velho**. É pior do que o descrito —
//   ninguém decide sobre uma troca que viu acontecer; decide-se sobre um ecrã parado que
//   parece actual. E na primeira escrita minha o conteúdo dos outros entra **de uma vez, sem
//   uma palavra**, misturado com o efeito do meu gesto.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ COMO SE DISTINGUE «FOI OUTRO» DE «FUI EU» — a decisão central, e a medição
// ---------------------------------------------------------------------------
//
//   **Pela notificação, não se distingue.** Medido: `anunciarMudancaDoPlano(c, origem)` leva um
//   `origem` que nomeia o **gesto** (`reservar:PD-1`, `mudar-condutor`, `copiar-plano`, …) em
//   29 pontos de chamada (27 pelo `emTransacaoDoPlano`, 2 directos) — e **nunca quem o fez**.
//   Não há utilizador, não há sessão, não há
//   separador. E de qualquer forma essa notificação **nunca sai da API**.
//
//   ✅ **A distinção que EXISTE é local, e é sólida:** este separador sabe exactamente o que o
//   ecrã dele está a mostrar — a **ETag** com que os dados vieram (`sha256` do conteúdo servido,
//   `servidor.ts`). Toda a escrita minha é seguida de um `recarregar`, logo a ETag do ecrã passa
//   a ser a nova. Uma ETag do servidor **diferente** da que está no ecrã quer dizer, sem
//   ambiguidade, *«o que está aqui já não é o plano»*.
//
//   ⚠️ **E é por isso que a frase diz «noutro sítio» e não «outra pessoa».** O que se sabe é que
//   **não foi este separador**; pode ter sido um colega, pode ter sido eu noutro separador, pode
//   ter sido uma cópia de plano a correr. Afirmar «outra pessoa» era pôr um valor plausível no
//   lugar do certo — a família de defeito que este projecto mede desde o início.
//
// ---------------------------------------------------------------------------
// ⚠️ AS ESCOLHAS, uma a uma
// ---------------------------------------------------------------------------
//
//   · **Não troca nada.** Diz, e oferece o `Actualizar` que já existe. Trocar por baixo de quem
//     está a decidir é o defeito; trocar por baixo dele **depois de avisar** continua a sê-lo.
//
//   · **A sonda é um GET condicional** (`If-None-Match`), porque é o que o servidor **já sabe
//     responder** — `servidor.ts` compara a ETag e devolve `304` sem corpo. Nada de rota nova.
//     ⚠️ Quando muda, a resposta é `200` e traz o mapa: **cancela-se o corpo** (`body.cancel()`)
//     mal se lê o estado, e **pára-se de sondar** enquanto o aviso estiver no ecrã. Custo total
//     de um aviso: uma resposta abortada, uma vez.
//
//   · **`sinal` nas dependências** — o mesmo truque do `Desfazer` e do `Kpis`. Qualquer recarga
//     do mapa dá um `dados` novo, o efeito volta a correr e o aviso apaga-se. ⚠️ Sem ele havia
//     um buraco real: se alguém desfizesse a alteração e o mapa voltasse ao **mesmo conteúdo**,
//     a ETag voltava a ser a mesma, o efeito não corria, e o aviso ficava preso para sempre —
//     que é exactamente o «aviso que dispara sempre» de que este projecto se protege.
//
//   · **Não se sonda sem ETag** (`etagNoEcra` a `null`). Sem base de comparação, a única resposta
//     honesta é o silêncio — e é o que mantém os testes que montam o `Mapa` com dados fixos sem
//     rede nenhuma.
//
//   · **Nada de `document.hidden`.** Os navegadores já limitam os temporizadores de um separador
//     escondido a ~1/minuto; acrescentar a condição pouparia quase nada e trazia um ouvinte de
//     `visibilitychange` para o caso — mais peças do que valor.
//
// 🅿️ **Declarado, e não corrigido:** entre o `COMMIT` de uma escrita MINHA e a chegada da
//    resposta que dispara o meu `recarregar` há uma janela de ~100–300 ms em que a sonda, se
//    calhar mesmo ali, vê a mudança como sendo de outro. **Cura-se sozinha** — a recarga que se
//    segue traz o `dados` novo e apaga o aviso — e a probabilidade é a razão entre essa janela e
//    os 30 s do intervalo. Não se fecha sem o componente saber quando há uma escrita em curso, e
//    isso obrigava a passar estado por 25 janelas para uma pisca de 200 ms.
// ============================================================================

import { useEffect, useState } from 'react';
import { BotaoActualizar } from './BotaoActualizar.js';
import { Diario } from './Diario.js';

/**
 * De quanto em quanto tempo se pergunta ao servidor.
 *
 * ⚠️ A resposta normal é um `304` — 2 ms no servidor, sem corpo. Com 20 separadores abertos dá
 * menos de um pedido por segundo, e é o preço de não deixar ninguém a decidir sobre um ecrã
 * velho.
 */
export const INTERVALO_SONDA_MS = 30_000;

export interface PlanoMudouProps {
  /**
   * A ETag com que vieram os dados que estão no ecrã. `null` enquanto não se souber — e então
   * não se sonda nem se afirma nada.
   */
  readonly etagNoEcra?: string | null | undefined;
  /**
   * ⚠️ **Não é dado: é o gatilho.** Um `dados` novo significa «o mapa foi recarregado», e é o
   * que apaga o aviso mesmo quando a ETag volta a ser a mesma. Ver o cabeçalho.
   */
  readonly sinal?: unknown;
  /** O `recarregar` do mapa — o mesmo que todas as janelas já usam. */
  readonly recarregar?: (() => void) | undefined;
  /** Existe para os testes não esperarem 30 s. Em produção nunca se passa. */
  readonly intervaloMs?: number;
}

export function PlanoMudou({
  etagNoEcra = null,
  sinal,
  recarregar,
  intervaloMs = INTERVALO_SONDA_MS,
}: PlanoMudouProps) {
  const [mudou, definirMudou] = useState(false);
  const [diarioAberto, definirDiarioAberto] = useState(false);

  useEffect(() => {
    // Qualquer recarga do mapa (ETag nova **ou** `sinal` novo) volta a pôr o ecrã em dia.
    definirMudou(false);
    if (etagNoEcra === null || etagNoEcra === '') return undefined;

    const abortar = new AbortController();
    /** Uma sonda de cada vez: com a rede lenta, duas sobrepostas mediriam a mesma coisa. */
    let aCorrer = false;
    /** Depois de avisar, pára — senão cada intervalo pedia o mapa outra vez para nada. */
    let jaAvisou = false;

    const sondar = async (): Promise<void> => {
      if (aCorrer || jaAvisou) return;
      aCorrer = true;
      try {
        const r = await fetch('/api/mapa/dados', {
          cache: 'no-store',
          headers: { 'if-none-match': etagNoEcra },
          signal: abortar.signal,
        });
        // ⚠️ O corpo cancela-se ANTES de qualquer decisão: num `200` ele é o mapa inteiro, e o
        //    que se queria era só o código de estado.
        try {
          await r.body?.cancel();
        } catch {
          /* o corpo já podia estar fechado — não é informação nenhuma */
        }
        if (r.status === 200) {
          jaAvisou = true;
          definirMudou(true);
        }
        // ⚠️ `304` é o caso normal e não diz nada. Qualquer outro estado (401, 503, …) também
        //    não: uma sonda que falha não sabe se o plano mudou, e «não sei» cala-se.
      } catch {
        /* falha de rede ou pedido abortado: ver acima — o silêncio é a resposta honesta */
      } finally {
        aCorrer = false;
      }
    };

    const temporizador = window.setInterval(() => void sondar(), intervaloMs);
    return () => {
      window.clearInterval(temporizador);
      abortar.abort();
    };
  }, [etagNoEcra, sinal, intervaloMs]);

  // ⚠️ A região viva existe SEMPRE, e é o conteúdo que aparece — um `aria-live` que só nasce
  //    com o texto lá dentro não é anunciado por leitor de ecrã nenhum. É o mesmo desenho do
  //    `.actualizar-quando` do `BotaoActualizar`, e pela mesma razão.
  // ⚠️⚠️ **`aria-live` e NÃO `role="status"`**: o cabeçalho do mapa já tem dois elementos com
  //    esse papel (`.filtro-activo` e `.topo-medidas`), e um terceiro parte as provas que
  //    procuram «o» status — foi o que aconteceu ao `Contas.test.tsx` quando o `BotaoActualizar`
  //    trouxe o `role`.
  return (
    <span className="plano-mudou" aria-live="polite">
      {mudou ? (
        <>
          <span className="plano-mudou-txt">
            O plano mudou noutro sítio — o que está no ecrã já não é o actual.
          </span>
          {/* ---- ⚠️⚠️ O AVISO PASSA A LEVAR AO DIÁRIO (2026-09-16) ----------------------
              🅿️ Era a ressalva escrita da melhoria **N** («o que mudou desde ontem»): *«falta o
              aviso dele LEVAR aqui (é `PlanoMudou.tsx`)»*. 📌 **Um aviso que não tem para onde
              levar é um beco** — «o plano mudou» sem «o quê» obriga a procurar a diferença numa
              grelha de 91 250 células, que ninguém faz.

              ⚠️⚠️ **E isto NÃO é fundir as duas peças, é ligá-las — a distinção é medida.**
              Este componente compara **ETags**, logo só sabe *«não foi este separador»*; o
              diário lê `auditoria.utilizador_id` e **sabe quem**. Partilhar formulação obrigava
              a que a afirmação mais fraca mandasse na mais forte: a frase daqui passaria a
              dizer «outra pessoa» sobre uma coisa que pode ter sido eu noutro separador. 📌 É a
              conclusão a que o agente da peça «desde ontem» já tinha chegado, e confirma-se.

              ⚠️ **Abre em «outros» e não em «tudo»** — quem carrega neste botão acabou de ler
              que a mudança **não foi deste separador**, e a lista que responde a isso é a dos
              outros. O selector do diário continua lá para alargar.

              ⚠️ Abre o diário GERAL (sem `alvo`): o aviso é sobre o plano inteiro e não sobre
              uma viagem, e um alvo inventado aqui filtrava a lista por uma célula que ninguém
              escolheu. */}
          <button
            type="button"
            className="btn btn--peq"
            onClick={() => definirDiarioAberto(true)}
            title="ver no diário o que mudou, e quem"
          >
            Ver o que mudou
          </button>
          {recarregar === undefined ? null : <BotaoActualizar actualizar={recarregar} />}
          {diarioAberto ? (
            <Diario
              quemInicial="outros"
              fechar={() => definirDiarioAberto(false)}
              {...(recarregar === undefined ? {} : { recarregarMapa: recarregar })}
            />
          ) : null}
        </>
      ) : null}
    </span>
  );
}
